// features/admin/userWalletApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface WalletUser {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  wallet_id: number | null;
  balance: string | number;
  total_credited: string | number;
  total_debited: string | number;
}

export interface WalletTransaction {
  id: number;
  wallet_id: number;
  user_id: number;
  user_name: string;
  order_id: number | null;
  return_id: number | null;
  amount: string | number;
  type: 'credit' | 'debit';
  source: string;
  created_at: string;
}

export interface WalletUsersResponse {
  error: boolean;
  message?: string;
  stats: {
    total_balance: number;
    total_credited: number;
    total_debited: number;
    users_with_balance: number;
  };
  count: number;
  data: WalletUser[];
}

export interface WalletTransactionsResponse {
  error: boolean;
  total: number;
  page: number;
  limit: number;
  data: WalletTransaction[];
}

export interface WalletTransactionsRequest {
  user_id?: number;
  type?: 'credit' | 'debit' | '';
  source?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const WALLET_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT,
  ENDPOINTS: {
    WALLET_USERS:        '/admin/admin-wallet-users',
    WALLET_TRANSACTIONS: '/admin/admin-wallet-transactions',
  },
};

// ─── API Slice ────────────────────────────────────────────────────────────────

export const userWalletApi = createApi({
  reducerPath: 'userWalletApi',
  baseQuery: fetchBaseQuery({
    baseUrl: WALLET_API_CONFIG.BASE_URL,
    prepareHeaders: async (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['WalletUsers', 'WalletTransactions'],
  endpoints: (build) => ({

    // ── List all users with wallet balance ──
    listWalletUsers: build.query<WalletUsersResponse, { search?: string } | void>({
      query: (params) => ({
        url: WALLET_API_CONFIG.ENDPOINTS.WALLET_USERS,
        method: 'POST',
        body: { search: params?.search || '' },
      }),
      providesTags: ['WalletUsers'],
      transformResponse: (response: any): WalletUsersResponse => {
        if (!response || response.error) {
          return {
            error: true,
            stats: { total_balance: 0, total_credited: 0, total_debited: 0, users_with_balance: 0 },
            count: 0,
            data: [],
          };
        }
        return {
          error:   response.error   ?? false,
          message: response.message || '',
          stats:   response.stats   || { total_balance: 0, total_credited: 0, total_debited: 0, users_with_balance: 0 },
          count:   response.count   || 0,
          data:    response.data    || [],
        };
      },
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to load wallet users',
        stats: { total_balance: 0, total_credited: 0, total_debited: 0, users_with_balance: 0 },
        count: 0,
        data: [],
      }),
    }),

    // ── Get wallet transactions (all or per user) ──
    listWalletTransactions: build.query<WalletTransactionsResponse, WalletTransactionsRequest>({
      query: (params) => ({
        url: WALLET_API_CONFIG.ENDPOINTS.WALLET_TRANSACTIONS,
        method: 'POST',
        body: {
          user_id:   params.user_id   || undefined,
          type:      params.type      || undefined,
          source:    params.source    || undefined,
          from_date: params.from_date || undefined,
          to_date:   params.to_date   || undefined,
          page:      params.page      || 1,
          limit:     params.limit     || 50,
        },
      }),
      providesTags: (_result, _error, { user_id }) =>
        user_id
          ? [{ type: 'WalletTransactions' as const, id: user_id }]
          : ['WalletTransactions'],
      transformResponse: (response: any): WalletTransactionsResponse => ({
        error: response.error   ?? false,
        total: response.total   || 0,
        page:  response.page    || 1,
        limit: response.limit   || 50,
        data:  response.data    || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true,
        message: response.data?.message || response.error || 'Failed to load transactions',
        total: 0, page: 1, limit: 50, data: [],
      }),
    }),

  }),
});

export const {
  useListWalletUsersQuery,
  useListWalletTransactionsQuery,
} = userWalletApi;